import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  activateOverride,
  clearOverride,
  createOverrideMessage,
  deleteOverrideMessage,
  getActiveOverrides,
  listContent,
  listDisplays,
  listOverrideMessages,
  seedOverrideMessages,
} from "../lib/resources.ts";
import type {
  ActiveOverride,
  Content,
  Display,
  OverrideMessage,
} from "../lib/types.ts";
import ContentPreview from "./ContentPreview.tsx";

// Emergency override (admin). Built phone-first: this is the one page in the CMS
// that gets used away from a desk — from a car at 6am, or standing in the foyer
// — so the whole flow is pick a message, pick screens, pick how long, confirm.
//
// Every push carries a duration. An override that runs "until cleared" relies on
// someone remembering, and the failure is silent: nobody notices a stale sign,
// they just read it and believe it. A bounded push fails the safe way instead —
// worst case you push it again.

const DURATIONS: { label: string; minutes: number | null }[] = [
  { label: "30 min", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "2 hours", minutes: 120 },
  { label: "4 hours", minutes: 240 },
  { label: "Until I clear it", minutes: null },
];
const DEFAULT_MINUTES = 120;

/** Coarse, human countdown — "42 min left". Deliberately not seconds-precise:
 *  the number is there to answer "will this clear itself?", not to be a timer. */
function formatLeft(expiresAt: string | null): string {
  if (!expiresAt) return "until cleared";
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "ending now";
  const mins = Math.round(ms / 60000);
  if (mins < 1) return "under a minute left";
  if (mins < 60) return `${mins} min left`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m left` : `${h}h left`;
}

export default function Override() {
  const [messages, setMessages] = useState<OverrideMessage[]>([]);
  const [active, setActive] = useState<ActiveOverride[]>([]);
  const [content, setContent] = useState<Content[]>([]);
  const [displays, setDisplays] = useState<Display[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Push sheet
  const [sheetFor, setSheetFor] = useState<OverrideMessage | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [allScreens, setAllScreens] = useState(false);
  const [minutes, setMinutes] = useState<number | null>(DEFAULT_MINUTES);
  const [armed, setArmed] = useState(false); // second tap for all-screens

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newContentId, setNewContentId] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [m, a, c, d] = await Promise.all([
        listOverrideMessages(),
        getActiveOverrides(),
        listContent(),
        listDisplays(),
      ]);
      setMessages(m);
      setActive(a);
      setContent(c);
      setDisplays(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Tick so the countdowns move without hammering the API.
  const [, setTick] = useState(0);
  const hasTimed = active.some((a) => a.expiresAt);
  useEffect(() => {
    if (!hasTimed) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 15_000);
    return () => window.clearInterval(id);
  }, [hasTimed]);

  // When the soonest override lapses, re-read once so the list matches the wall.
  const soonest = useMemo(() => {
    const times = active
      .map((a) => a.expiresAt)
      .filter((x): x is string => Boolean(x))
      .map((x) => new Date(x).getTime())
      .filter((t) => Number.isFinite(t));
    return times.length ? Math.min(...times) : null;
  }, [active]);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    if (soonest === null) return;
    const delay = Math.max(1000, soonest - Date.now() + 1500);
    const id = window.setTimeout(() => void refreshRef.current(), delay);
    return () => window.clearTimeout(id);
  }, [soonest]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  const nameOf = useCallback(
    (id: string) => displays.find((d) => d.id === id)?.name ?? "a screen",
    [displays],
  );

  function openSheet(m: OverrideMessage) {
    setSheetFor(m);
    setPicked(new Set());
    setAllScreens(false);
    setMinutes(DEFAULT_MINUTES);
    setArmed(false);
  }
  function closeSheet() {
    setSheetFor(null);
    setArmed(false);
  }

  function toggleScreen(id: string) {
    setAllScreens(false);
    setArmed(false);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const targetCount = allScreens ? displays.length : picked.size;
  const canPush = targetCount > 0;
  const durationLabel =
    DURATIONS.find((d) => d.minutes === minutes)?.label ?? "";
  const pushLabel = !canPush
    ? "Choose a screen"
    : allScreens
      ? `Push to all ${displays.length} screens`
      : picked.size === 1
        ? `Push to ${nameOf([...picked][0])}`
        : `Push to ${picked.size} screens`;

  async function doPush() {
    if (!sheetFor || !canPush) return;
    // An all-screens takeover reaches the whole building from a phone, where a
    // mis-tap is a scroll away. Make it deliberate.
    if (allScreens && !armed) {
      setArmed(true);
      return;
    }
    await run(() =>
      activateOverride({
        contentId: sheetFor.contentId,
        scope: allScreens ? "all" : "subset",
        ...(allScreens ? {} : { displayIds: [...picked] }),
        durationMinutes: minutes,
      }),
    );
    closeSheet();
  }

  return (
    <section className="admin-page ovr">
      <div className="page-head">
        <div>
          <h1>Emergency Override</h1>
          <p className="muted">
            Take over a screen — or every screen — with a saved message. Each
            push runs for as long as you choose, then the screens go back to
            normal on their own.
          </p>
        </div>
        <div className="page-actions">
          {messages.length === 0 && !loading && (
            <button
              type="button"
              className="btn btn--ghost"
              disabled={busy}
              onClick={() => run(seedOverrideMessages)}
            >
              Load default messages
            </button>
          )}
        </div>
      </div>

      {error && <div className="banner banner--error">{error}</div>}

      {/* ---- What's live right now ---- */}
      {active.length > 0 && (
        <div className="ovr-live">
          <div className="ovr-live-head">
            <span className="ovr-dot" aria-hidden="true" />
            <strong>
              {active.length === 1 ? "1 override live" : `${active.length} overrides live`}
            </strong>
            <button
              type="button"
              className="btn btn--danger ovr-clear-all"
              disabled={busy}
              onClick={() => run(() => clearOverride())}
            >
              Clear all
            </button>
          </div>
          <ul className="ovr-live-list">
            {active.map((a) => (
              <li className="ovr-live-item" key={a.id}>
                <div className="ovr-live-thumb">
                  <ContentPreview content={a.content} />
                </div>
                <div className="ovr-live-text">
                  <span className="ovr-live-name">{a.content.name}</span>
                  <span className="muted ovr-live-where">
                    {a.scope === "all"
                      ? "All screens"
                      : (a.displayIds ?? []).map(nameOf).join(", ")}
                  </span>
                  <span
                    className={
                      "ovr-remaining" + (a.expiresAt ? "" : " ovr-remaining--open")
                    }
                  >
                    {formatLeft(a.expiresAt)}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={busy}
                  onClick={() => run(() => clearOverride(a.id))}
                >
                  Clear
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---- Pick a message ---- */}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : messages.length === 0 ? (
        <div className="admin-empty">
          <strong>No saved messages</strong>
          <span>Load the defaults above, or create one from a content item.</span>
        </div>
      ) : (
        <>
          <h2 className="ovr-section-title">Push a message</h2>
          <ul className="ovr-messages">
            {messages.map((m) => (
              <li className="ovr-msg" key={m.id}>
                <button
                  type="button"
                  className="ovr-msg-main"
                  disabled={busy}
                  onClick={() => openSheet(m)}
                >
                  <span className="ovr-msg-thumb">
                    <ContentPreview content={m.content} />
                  </span>
                  <span className="ovr-msg-name">{m.name}</span>
                  <span className="ovr-msg-go" aria-hidden="true">›</span>
                </button>
                <button
                  type="button"
                  className="link-btn link-btn--danger ovr-msg-del"
                  disabled={busy}
                  onClick={() => run(() => deleteOverrideMessage(m.id))}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* ---- Create a message (secondary — collapsed on phones) ---- */}
      <div className="ovr-create">
        <button
          type="button"
          className="link-btn"
          aria-expanded={showCreate}
          onClick={() => setShowCreate((v) => !v)}
        >
          {showCreate ? "− " : "+ "}New message from content
        </button>
        {showCreate && (
          <div className="ovr-create-row">
            <input
              className="inline-input"
              placeholder="Message name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <select
              aria-label="Content for the new message"
              value={newContentId}
              onChange={(e) => setNewContentId(e.target.value)}
            >
              <option value="">Select content…</option>
              {content.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.type})
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              disabled={busy || !newName.trim() || !newContentId}
              onClick={() =>
                run(async () => {
                  await createOverrideMessage(newName.trim(), newContentId);
                  setNewName("");
                  setNewContentId("");
                  setShowCreate(false);
                })
              }
            >
              Save message
            </button>
          </div>
        )}
      </div>

      {/* ---- Push sheet: screens, then how long, then confirm ---- */}
      {sheetFor && (
        <div className="modal-scrim ovr-scrim" onClick={closeSheet}>
          <div
            className="ovr-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={`Push ${sheetFor.name}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ovr-sheet-grip" aria-hidden="true" />

            <div className="ovr-sheet-head">
              <div className="ovr-sheet-thumb">
                <ContentPreview content={sheetFor.content} />
              </div>
              <div>
                <strong>{sheetFor.name}</strong>
                <p className="muted">Choose where it goes and how long it stays.</p>
              </div>
            </div>

            <div className="ovr-sheet-body">
              <fieldset className="ovr-group">
                <legend>Which screens?</legend>
                <div className="ovr-chips">
                  <button
                    type="button"
                    className={"ovr-chip" + (allScreens ? " ovr-chip--on" : "")}
                    aria-pressed={allScreens}
                    onClick={() => {
                      setAllScreens((v) => !v);
                      setPicked(new Set());
                      setArmed(false);
                    }}
                  >
                    All screens
                  </button>
                  {displays.map((d) => (
                    <button
                      type="button"
                      key={d.id}
                      className={
                        "ovr-chip" + (picked.has(d.id) ? " ovr-chip--on" : "")
                      }
                      aria-pressed={picked.has(d.id)}
                      onClick={() => toggleScreen(d.id)}
                    >
                      {d.name}
                    </button>
                  ))}
                </div>
                {displays.length === 0 && (
                  <p className="muted">No displays registered.</p>
                )}
              </fieldset>

              <fieldset className="ovr-group">
                <legend>How long?</legend>
                <div className="ovr-chips">
                  {DURATIONS.map((d) => (
                    <button
                      type="button"
                      key={d.label}
                      className={
                        "ovr-chip" + (minutes === d.minutes ? " ovr-chip--on" : "")
                      }
                      aria-pressed={minutes === d.minutes}
                      onClick={() => {
                        setMinutes(d.minutes);
                        setArmed(false);
                      }}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
                <p className="muted ovr-hint">
                  {minutes === null
                    ? "It stays until someone clears it. Nothing will remove it for you."
                    : `It clears itself after ${durationLabel.toLowerCase()} — you can clear it sooner.`}
                </p>
              </fieldset>
            </div>

            <div className="ovr-sheet-actions">
              <button type="button" className="btn btn--ghost" onClick={closeSheet}>
                Cancel
              </button>
              <button
                type="button"
                className={"btn ovr-push" + (armed ? " btn--danger" : "")}
                disabled={busy || !canPush}
                onClick={doPush}
              >
                {armed ? "Tap again to confirm" : pushLabel}
              </button>
            </div>
            {armed && (
              <p className="ovr-armed-note">
                This replaces what every screen in the building is showing.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
